import React, { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "paid", "in_delivery", "in_rental", "to_return", "returned", "cancelled"];
const STATUS_LABEL = { pending: "En attente", confirmed: "Confirmée", paid: "Payée", in_delivery: "En livraison", in_rental: "En location", to_return: "À récupérer", returned: "Terminée", cancelled: "Annulée" };
const badge = { pending: "bg-slate-100 text-slate-600", confirmed: "bg-blue-100 text-blue-700", paid: "bg-green-100 text-green-700", in_delivery: "bg-amber-100 text-amber-700", in_rental: "bg-brand/10 text-brand", to_return: "bg-orange-100 text-orange-700", returned: "bg-slate-100 text-slate-500", cancelled: "bg-red-100 text-red-700" };

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState([]);

  const load = () => api.get("/admin/bookings").then((r) => { setBookings(r.data); setLoading(false); });
  useEffect(() => {
    load();
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  if (loading) return <div className="grid place-items-center h-64"><Loader2 className="w-10 h-10 animate-spin text-brand" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-extrabold text-3xl text-ink">Réservations</h1>
        <Button data-testid="new-manual-booking" onClick={() => setShowManual(true)} className="rounded-full h-12 px-6 bg-brand text-white font-bold"><Plus className="w-5 h-5 mr-1" /> Réservation manuelle</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[900px]">
          <thead className="bg-slate-50 text-ink-muted text-sm">
            <tr><th className="p-4">Réf</th><th className="p-4">Client</th><th className="p-4">Ville</th><th className="p-4">Dates</th><th className="p-4">Total</th><th className="p-4">Paiement</th><th className="p-4">Statut</th></tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} className="border-t border-slate-50" data-testid={`booking-row-${b.ref}`}>
                <td className="p-4 font-semibold text-ink">{b.ref}</td>
                <td className="p-4">{b.first_name} {b.last_name}<br /><span className="text-sm text-ink-muted">{b.phone}</span></td>
                <td className="p-4">{b.city_name}</td>
                <td className="p-4 text-sm">{b.start.slice(0, 10)} → {b.end.slice(0, 10)}</td>
                <td className="p-4 font-bold text-brand">{b.total.toFixed(2)} €</td>
                <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{b.payment_status === "paid" ? "Payé" : "En attente"}</span></td>
                <td className="p-4">
                  <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} data-testid={`booking-status-${b.ref}`} className={`rounded-lg px-2 py-1.5 text-sm font-semibold border-0 ${badge[b.status]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && <p className="p-8 text-center text-ink-muted">Aucune réservation.</p>}
      </div>

      {showManual && <ManualDialog cities={cities} products={products} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); load(); }} />}
    </div>
  );
};

const ManualDialog = ({ cities, products, onClose, onDone }) => {
  const [f, setF] = useState({ city_id: cities[0]?.id || "", product_id: products[0]?.id || "", start: "", end: "", quantity: 1, first_name: "", last_name: "", email: "", phone: "", delivery_type: "hotel", delivery_details: {}, notes: "", paid: false, status: "confirmed" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/bookings/manual", { ...f, quantity: Number(f.quantity), delivery_details: { address: f._addr || "" }, lang: "fr" });
      toast.success("Réservation créée");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h2 className="font-heading font-bold text-2xl">Réservation téléphone</h2><button onClick={onClose}><X className="w-6 h-6" /></button></div>
        <div className="space-y-3">
          <select value={f.city_id} onChange={(e) => set("city_id", e.target.value)} className="w-full h-12 rounded-xl border-2 border-slate-200 px-3">{cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          <select value={f.product_id} onChange={(e) => set("product_id", e.target.value)} className="w-full h-12 rounded-xl border-2 border-slate-200 px-3">{products.map((p) => <option key={p.id} value={p.id}>{p.name.fr}</option>)}</select>
          <div className="grid grid-cols-2 gap-3">
            <input type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
            <input type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Prénom" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
            <input placeholder="Nom" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
            <input placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
            <input placeholder="Téléphone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="h-12 rounded-xl border-2 border-slate-200 px-3" />
          </div>
          <input placeholder="Adresse de livraison" onChange={(e) => set("_addr", e.target.value)} className="w-full h-12 rounded-xl border-2 border-slate-200 px-3" />
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.paid} onChange={(e) => set("paid", e.target.checked)} /> Marquer comme payé</label>
          <Button onClick={save} disabled={saving} className="w-full h-12 rounded-full bg-brand text-white font-bold">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer"}</Button>
        </div>
      </div>
    </div>
  );
};

export default Bookings;
